<template>
  <section class="container-fluid p-0 bodyArea">
    <div class="containerBox">
      <div class="pageHeader row align-items-center" v-if="$auth.isAuthenticated">
        <div class="col-3 pageHeaderTitle">Smart OR Management</div>
        <div class="col text-right">
          <div class="filterWrapper">
            <div class="filterText">Tenant :</div>
            <div class="dropdown col p-0">
              <model-select v-model="lastSelectedTenantObj" :options="tenants" name="text" id="tenant-select"
                placeholder="Select Tenant" @input="validateSelection" class="tenantList">
              </model-select>
            </div>
          </div>
          <div class="filterWrapper">
            <div class="filterText">Facility :</div>
            <div class="dropdown col p-0">
              <model-select v-model="lastSelectedFacilityObj" :options="facilities" name="text" id="facility-select"
                placeholder="Select Facility" @input="validateFacilitySelection" class="tenantList">
              </model-select>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-12">
          <div class="content-section">
            <div class="settings-section" v-if="selectedTenantOpt && selectedFacilityOpt">
              <div v-if="isLoadingSettings" class="loading-state">Loading settings...</div>

              <div v-else>
                <h5 class="settings-title">AI Detections</h5>
                <div class="settings-options-list">
                  <label class="option-toggle">
                    <input type="checkbox" v-model="smartORSettings.aiDetectionsEnabled" />
                    <span class="option-label">Enable AI Detections</span>
                  </label>
                </div>

                <h5 class="settings-title">Data Collection</h5>
                <div class="settings-options-list">
                  <label class="option-toggle">
                    <input type="checkbox" v-model="smartORSettings.dataCollectionEnabled" />
                    <span class="option-label">Enable Data Collection</span>
                  </label>
                </div>

                <p class="field-hint" v-if="!smartORSettings.dataCollectionEnabled">
                  Enable data collection to configure active collection hours.
                </p>

                <div class="data-config-layout" v-if="smartORSettings.dataCollectionEnabled">
                  <div class="config-card">
                    <h6 class="config-card-title">Collection Window</h6>
                    <div class="time-fields-grid">
                      <div class="field-group">
                        <label for="data-collection-start">Start Time</label>
                        <input id="data-collection-start" type="time" class="form-control"
                          v-model="smartORSettings.dataCollectionStartTime" @input="validateSettings"
                          @change="validateSettings" />
                        <div class="field-error" v-if="liveValidationErrors.dataCollectionStartTime">
                          {{ liveValidationErrors.dataCollectionStartTime }}
                        </div>
                      </div>
                      <div class="field-group">
                        <label for="data-collection-end">End Time</label>
                        <input id="data-collection-end" type="time" class="form-control"
                          v-model="smartORSettings.dataCollectionEndTime" @input="validateSettings"
                          @change="validateSettings" />
                        <div class="field-error" v-if="liveValidationErrors.dataCollectionEndTime">
                          {{ liveValidationErrors.dataCollectionEndTime }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="config-card">
                    <h6 class="config-card-title">Collection Days</h6>
                    <div class="field-group">
                      <treeselect
                        v-model="selectedDaysTree"
                        :options="daysTreeOptions"
                        :multiple="true"
                        :flat="true"
                        @input="onDaysSelectionChange"
                        placeholder="Select collection days"
                        class="tree-select-days" />
                    </div>
                  </div>
                </div>

                <h5 class="settings-title">Camera PTZ</h5>
                <div class="config-card mt-3">
                  <div class="ptz-inline-row">
                    <h6 class="config-card-title">Secondary Device PTZ</h6>
                    <label for="ptz-secondary-return-home" class="ptz-inline-label">Return To Home Duration
                      (minutes)</label>
                    <input id="ptz-secondary-return-home" type="text" inputmode="numeric" maxlength="1"
                      class="form-control duration-input ptz-inline-input"
                      v-model="smartORSettings.secondaryDevicePtzReturnHomeMinutes" @keypress="allowDigitKeypress"
                      @input="onReturnHomeDurationInput($event, 'secondaryDevicePtzReturnHomeMinutes')" />
                    <small class="field-hint ptz-inline-hint">Allowed range: 0 to 5</small>
                  </div>
                  <div class="field-error" v-if="liveValidationErrors.secondaryDevicePtzReturnHomeMinutes">
                    {{ liveValidationErrors.secondaryDevicePtzReturnHomeMinutes }}
                  </div>
                </div>

                <div class="button-row">
                  <button class="btn btn-primary" :disabled="isSavingSettings" @click.prevent="saveSmartORSettings">
                    {{ isSavingSettings ? "Saving..." : "Save Settings" }}
                  </button>
                </div>
              </div>
            </div>

            <div class="alert alert-warning" role="alert" v-else>
              Select a tenant and facility to load Smart OR settings.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/* global config */
/* eslint-disable no-console */
import Vue from "vue";
import VueToast from "vue-toast-notification";
import "vue-toast-notification/dist/theme-sugar.css";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { ModelSelect } from "vue-search-select";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import SmartORSettingsAPIService from "../shared/services/api/SmartORSettings.service";
import LocationAPIService from "../shared/services/api/Locations.service";
import "vue-search-select/dist/VueSearchSelect.css";

Vue.use(VueToast);
Vue.use(Loading);

export default {
  name: 'SmartORManager',
  components: {
    ModelSelect,
    Treeselect
  },
  watch: {
    "smartORSettings.dataCollectionStartTime"() {
      this.validateSettings();
    },
    "smartORSettings.dataCollectionEndTime"() {
      this.validateSettings();
    }
  },
  data() {
    return {
      tenants: [{ text: "-SELECT-", value: "" }],
      facilities: [{ text: "-SELECT-", value: "" }],
      lastSelectedTenant: "",
      lastSelectedTenantObj: "",
      lastSelectedFacility: "",
      lastSelectedFacilityObj: "",
      selectedTenantOpt: "",
      selectedFacilityOpt: "",
      smartORSettings: {
        dataCollectionEnabled: false,
        dataCollectionStartTime: "00:00",
        dataCollectionEndTime: "23:59",
        dataCollectionDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        primaryDevicePtzReturnHomeMinutes: 0,
        secondaryDevicePtzReturnHomeMinutes: 0,
        aiDetectionsEnabled: false
      },
      selectedDaysTree: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      validationErrors: {},
      isLoadingSettings: false,
      isSavingSettings: false,
      daysTreeOptions: [
        { id: "Monday", label: "Monday" },
        { id: "Tuesday", label: "Tuesday" },
        { id: "Wednesday", label: "Wednesday" },
        { id: "Thursday", label: "Thursday" },
        { id: "Friday", label: "Friday" },
        { id: "Saturday", label: "Saturday" },
        { id: "Sunday", label: "Sunday" }
      ]
    };
  },
  computed: {
    liveValidationErrors() {
      const errors = {};
      const startTime = this.smartORSettings.dataCollectionStartTime;
      const endTime = this.smartORSettings.dataCollectionEndTime;
      const dataCollectionEnabled = this.smartORSettings.dataCollectionEnabled;
      const primaryDurationValue = this.smartORSettings.primaryDevicePtzReturnHomeMinutes;
      const secondaryDurationValue = this.smartORSettings.secondaryDevicePtzReturnHomeMinutes;
      const startTimeInMinutes = this.parseTimeToMinutes(startTime);
      const endTimeInMinutes = this.parseTimeToMinutes(endTime);

      if (dataCollectionEnabled) {
        if (startTimeInMinutes === null) {
          errors.dataCollectionStartTime = "Start time must be HH:mm or h:mm AM/PM.";
        }

        if (endTimeInMinutes === null) {
          errors.dataCollectionEndTime = "End time must be HH:mm or h:mm AM/PM.";
        }

        if (!errors.dataCollectionStartTime && !errors.dataCollectionEndTime) {
          if (startTimeInMinutes >= endTimeInMinutes) {
            errors.dataCollectionStartTime = "Start time must be less than end time.";
            errors.dataCollectionEndTime = "End time must be greater than start time.";
          }
        }
      }

      const primaryDurationError = this.getDurationValidationError(primaryDurationValue);
      if (primaryDurationError) {
        errors.primaryDevicePtzReturnHomeMinutes = primaryDurationError;
      }

      const secondaryDurationError = this.getDurationValidationError(secondaryDurationValue);
      if (secondaryDurationError) {
        errors.secondaryDevicePtzReturnHomeMinutes = secondaryDurationError;
      }

      return errors;
    }
  },
  methods: {
    showToast(message, type = "default") {
      Vue.$toast.open({
        message,
        type,
        position: "top",
      });
    },
    getDurationValidationError(durationValue) {
      if (durationValue === "" || durationValue === null || durationValue === undefined) {
        return "Return to home duration is required.";
      }

      const minutes = Number(durationValue);
      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 5) {
        return "Value must be an integer between 0 and 5.";
      }

      return "";
    },
    async fetchTenants() {
      let loader = Vue.$loading.show({
        color: "skyblue",
        opacity: 0.1,
        loader: "dots"
      });

      try {
        const claims = await this.$auth.getIdTokenClaims();
        const id_token = claims.__raw;
        const tenants_list_request = new Request(
          config.TENET_SERVICE + "/tenants/?list=true",
          {
            method: "GET",
            mode: "cors",
            cache: "default",
            withCredentials: true,
            credentials: "include",
            headers: {
              Authorization: "Bearer " + id_token,
              "Content-Type": "application/json"
            }
          }
        );
        const tenants_list = await fetch(tenants_list_request);
        
        // Handle authentication errors
        if (tenants_list.status === 401) {
          console.error("fetchTenants: Unauthorized - session expired or invalid token");
          this.tenants = [{ text: "-SELECT-", value: "" }];
          this.showToast("Your session has expired. Please log in again.", "error");
          return;
        }
        
        if (tenants_list.status === 403) {
          console.error("fetchTenants: Forbidden - insufficient permissions");
          this.tenants = [{ text: "-SELECT-", value: "" }];
          this.showToast("You don't have permission to access tenants.", "error");
          return;
        }
        
        // Handle service unavailable with retry logic
        if (tenants_list.status === 503) {
          if ((this._fetchTenantsRetries = (this._fetchTenantsRetries || 0) + 1) <= 1) {
            loader.hide();
            return this.fetchTenants();
          }
          console.error("fetchTenants: Service unavailable - max retries exceeded");
          this.tenants = [{ text: "-SELECT-", value: "" }];
          this.showToast("Service temporarily unavailable. Please try again later.", "error");
          return;
        }
        
        // Handle other HTTP errors
        if (tenants_list.status !== 200) {
          console.error("fetchTenants: HTTP", tenants_list.status, tenants_list.statusText);
          this.tenants = [{ text: "-SELECT-", value: "" }];
          this.showToast(`Failed to load tenants (${tenants_list.status}). Please try again.`, "error");
          return;
        }
        
        const response = await tenants_list.json();
        this.tenants = response
          .map((t) => {
            return {
              text: t.text || t.value,
              id: t.value,
              value: t.value.toLowerCase().replaceAll("-", "")
            };
          })
          .sort((a, b) => {
            return a.text.localeCompare(b.text);
          });
      } catch (error) {
        console.error("Error fetching tenants:", error);
        this.tenants = [{ text: "-SELECT-", value: "" }];
        this.showToast("Failed to load tenants. Please try again.", "error");
      } finally {
        loader.hide();
      }
    },
    validateSelection() {
      this.lastSelectedTenant = this.getTenantId(this.lastSelectedTenantObj);
      let selectedTenant = this.tenants.find(e => e.value == this.lastSelectedTenantObj);
      this.selectedTenantOpt = selectedTenant ? selectedTenant.text : "";
      this.facilities = [{ text: "-SELECT-", value: "" }];
      this.lastSelectedFacility = "";
      this.lastSelectedFacilityObj = "";
      this.selectedFacilityOpt = "";
      this.resetSettings();
      if (this.lastSelectedTenant) {
        this.fetchLocations();
      }
    },
    validateFacilitySelection() {
      this.lastSelectedFacility = this.getFacilityId(this.lastSelectedFacilityObj);
      let selectedFacility = this.facilities.find(e => e.value == this.lastSelectedFacilityObj);
      this.selectedFacilityOpt = selectedFacility ? selectedFacility.text : "";
      this.resetSettings();
      if (this.lastSelectedTenant && this.lastSelectedFacility) {
        this.loadFacilitySettings();
      }
    },
    getTenantId(Obj) {
      let objVal = this.tenants.find(e => e.value == Obj);
      if (objVal) {
        return objVal.id;
      } else {
        return "";
      }
    },
    getFacilityId(obj) {
      let objVal = this.facilities.find(e => e.value == obj);
      if (objVal) {
        return objVal.id;
      } else {
        return "";
      }
    },
    async fetchLocations() {
      if (!this.lastSelectedTenant) {
        return;
      }

      let loader = Vue.$loading.show({
        color: "skyblue",
        opacity: 0.1,
        loader: "dots"
      });

      try {
        const response = await LocationAPIService.fetchLocations({
          tenantName: this.lastSelectedTenant,
          limit: 500,
          skip: 0
        });

        const locations = Array.isArray(response) ? response : [];
        this.facilities = locations
          .map((location) => {
            const locationId = location.locationId || location.id || location.value;
            const locationName = location.name || location.text || String(locationId || "");
            return {
              text: locationName,
              id: locationId,
              value: locationId
            };
          })
          .filter((location) => location.id)
          .sort((a, b) => a.text.localeCompare(b.text));
      } catch (error) {
        console.error("Error loading facilities:", error);
        this.facilities = [{ text: "-SELECT-", value: "" }];
        this.showToast("Failed to load facilities.", "error");
      } finally {
        loader.hide();
      }
    },
    onDaysSelectionChange(selectedData) {
      // selectedData contains day name strings from tree select
      // Store selected day names as array
      this.smartORSettings.dataCollectionDays = selectedData;
    },
    getDefaultSettings() {
      return {
        dataCollectionEnabled: false,
        dataCollectionStartTime: "00:00",
        dataCollectionEndTime: "23:59",
        dataCollectionDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        primaryDevicePtzReturnHomeMinutes: 0,
        secondaryDevicePtzReturnHomeMinutes: 0,
        aiDetectionsEnabled: false
      };
    },
    initializeDaysTreeSelection() {
      const days = this.smartORSettings.dataCollectionDays || [];
      this.selectedDaysTree = days;
    },
    resetSettings() {
      this.smartORSettings = this.getDefaultSettings();
      this.initializeDaysTreeSelection();
      this.validationErrors = {};
    },
    validateSettings() {
      this.validationErrors = { ...this.liveValidationErrors };
      return Object.keys(this.validationErrors).length === 0;
    },
    parseTimeToMinutes(timeValue) {
      if (timeValue === null || timeValue === undefined) {
        return null;
      }

      const normalized = String(timeValue).trim();
      if (!normalized) {
        return null;
      }

      const match24Hour = normalized.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
      if (match24Hour) {
        const hours = Number(match24Hour[1]);
        const minutes = Number(match24Hour[2]);
        return (hours * 60) + minutes;
      }

      const match12Hour = normalized.match(/^([1-9]|1[0-2])(?::([0-5]\d))?\s*([AaPp][Mm])$/);
      if (match12Hour) {
        let hours = Number(match12Hour[1]);
        const minutes = Number(match12Hour[2] || 0);
        const meridiem = match12Hour[3].toUpperCase();

        if (meridiem === "AM") {
          hours = hours === 12 ? 0 : hours;
        } else {
          hours = hours === 12 ? 12 : hours + 12;
        }

        return (hours * 60) + minutes;
      }

      return null;
    },
    allowDigitKeypress(event) {
      const charCode = event.which || event.keyCode;
      const isDigit = charCode >= 48 && charCode <= 57;
      if (!isDigit) {
        event.preventDefault();
      }
    },
    onReturnHomeDurationInput(event, fieldName) {
      let value = String(event && event.target ? event.target.value : "");
      value = value.replace(/\D/g, "");

      if (value.length > 1) {
        value = value.slice(0, 1);
      }

      const numericValue = Number(value);
      if (value !== "" && numericValue > 5) {
        value = "5";
      }

      this.smartORSettings[fieldName] = value;
      this.validateSettings();
    },
    async loadFacilitySettings() {
      if (!this.lastSelectedTenant || !this.lastSelectedFacility) {
        return;
      }

      let loader = Vue.$loading.show({
        color: "skyblue",
        opacity: 0.1,
        isFullPage: false,
        loader: "dots"
      });

      this.isLoadingSettings = true;
      this.validationErrors = {};

      try {
        const response = await SmartORSettingsAPIService.getSmartORSettings(
          this.lastSelectedTenant,
          this.lastSelectedFacility
        );

        const apiSettings = response && response.result ? response.result : {};
        this.smartORSettings = {
          ...this.getDefaultSettings(),
          ...apiSettings
        };
        
        // Initialize selectedDaysTree with correct state
        this.initializeDaysTreeSelection();
      } catch (error) {
        console.error("Error loading Smart OR settings:", error);
        this.smartORSettings = this.getDefaultSettings();
        this.initializeDaysTreeSelection();
        this.showToast("Failed to load settings. Using defaults.", "error");
      } finally {
        this.isLoadingSettings = false;
        loader.hide();
      }
    },
    async saveSmartORSettings() {
      if (!this.lastSelectedTenant || !this.lastSelectedFacility) {
        this.showToast("Please select tenant and facility first.", "error");
        return;
      }

      if (!this.validateSettings()) {
        this.showToast("Please fix validation errors before saving.", "error");
        return;
      }

      let loader = Vue.$loading.show({
        color: "skyblue",
        opacity: 0.1,
        isFullPage: false,
        loader: "dots"
      });

      this.isSavingSettings = true;

      const payload = {
        ...this.smartORSettings,
        primaryDevicePtzReturnHomeMinutes: Number(this.smartORSettings.primaryDevicePtzReturnHomeMinutes),
        secondaryDevicePtzReturnHomeMinutes: Number(this.smartORSettings.secondaryDevicePtzReturnHomeMinutes)
      };

      try {
        const response = await SmartORSettingsAPIService.updateSmartORSettings(
          this.lastSelectedTenant,
          this.lastSelectedFacility,
          payload
        );

        if (!response || response.error) {
          this.showToast("Failed to save Smart OR settings.", "error");
          return;
        }

        this.showToast("Smart OR settings saved successfully.", "success");
      } catch (error) {
        console.error("Error saving Smart OR settings:", error);
        this.showToast("Failed to save Smart OR settings.", "error");
      } finally {
        this.isSavingSettings = false;
        loader.hide();
      }
    }
  },
  mounted() {
    this.fetchTenants();
  }
};
</script>

<style scoped>
@font-face {
  font-family: GTWalsheimPro-Regular;
  src: url(../assets/fonts/GTWalsheimPro-Regular.eot);
  src: url(../assets/fonts/GTWalsheimPro-Regular.eot) format("embedded-opentype"),
    url(../assets/fonts/GTWalsheimPro-Regular.ttf) format("truetype"),
    url(../assets/fonts/GTWalsheimPro-Regular.svg) format("svg");
  font-weight: normal;
  font-style: normal;
}

::v-deep .ui.search.dropdown .text {
  max-width: 230px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  height: 15px;
  color: #fff !important;
}

::v-deep .ui.selection.dropdown {
  border: none !important;
}

::v-deep .ui.selection.dropdown i.dropdown.icon {
  display: none;
}

*,
:after,
:before {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

body,
html {
  font-family: GTWalsheimPro-Regular, sans-serif !important;
  background: #fff !important;
  font-size: 16px;
  overflow-x: hidden;
}

b,
strong {
  font-weight: 500;
}

button,
html input[type="button"],
input[type="reset"],
input[type="submit"] {
  -webkit-appearance: button;
  appearance: auto;
  cursor: pointer;
}

button[disabled],
html input[disabled] {
  cursor: default;
}

button::-moz-focus-inner,
input::-moz-focus-inner {
  border: 0;
  padding: 0;
  outline: 0 !important;
}

a,
a img,
a:active,
a:focus,
button:focus,
a:hover {
  outline: 0 !important;
  -webkit-transition: all 0.35s;
  -moz-transition: all 0.35s;
  transition: all 0.35s;
  text-decoration: none;
  cursor: pointer;
}

a:hover {
  text-decoration: underline;
}

img {
  border: 0 !important;
}

a:focus:not(:hover),
button:focus:not(:hover) {
  outline: 0;
}

::-webkit-input-placeholder {
  color: rgba(4, 78, 89, 1) !important;
}

::-moz-placeholder {
  color: rgba(4, 78, 89, 1) !important;
}

:-ms-input-placeholder {
  color: rgba(4, 78, 89, 1) !important;
}

:-moz-placeholder {
  color: rgba(4, 78, 89, 1) !important;
}

input[type="text"],
input[type="email"],
input[type="search"],
input[type="number"],
input[type="password"],
input[type="tel"],
input[type="date"],
select.form-control,
textarea.form-control {
  box-shadow: none;
  background: transparent;
  outline: none !important;
  border: 1px solid #dceaea;
  border-radius: 5px;
  width: 100%;
  padding: 7px 15px;
  color: #6a6a6a;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="search"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="tel"]:focus,
input[type="date"]:focus,
select.form-control:focus,
textarea.form-control:focus {
  box-shadow: none;
  background: transparent;
  outline: none !important;
}

input[type="text"]:hover,
input[type="email"]:hover,
input[type="search"]:hover,
input[type="number"]:hover,
input[type="password"]:hover,
input[type="tel"]:hover,
input[type="date"]:hover,
select.form-control:hover,
textarea.form-control:hover {
  box-shadow: none;
  background: #fff;
  outline: none !important;
}

.bodyArea {
  background-color: #f8f9fa;
  min-height: calc(100vh - 80px);
}

.containerBox {
  padding: 0;
}

.pageHeader {
  height: 56px;
  color: #fff;
  background: #484848;
  font-size: 15px;
  padding: 0 15px;
}

.pageHeaderTitle {
  color: #fff;
  font-size: 15px;
  font-weight: 400;
  margin: 0;
  padding: 0;
  padding-left: 15px !important;
  line-height: 56px;
}

.filterWrapper {
  background: #575757;
  display: inline-flex;
  padding: 0px 10px;
  font-size: 16px;
  min-width: 360px;
  max-width: 360px;
  border-radius: 5px;
  margin-left: 15px;
  align-items: center;
  text-overflow: ellipsis;
}

.filterText {
  font-weight: 400;
  color: #fff;
  margin: 0;
  white-space: nowrap;
}

.dropdown {
  min-width: 200px;
  padding: 0;
}

.tenantList {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 12px;
  background-color: white;
}

.text-right {
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.content-section {
  background: white;
  padding: 30px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0 30px;
}

.btn-primary {
  background-color: #1dd8f6;
  border-color: #1dd8f6;
  color: #0b2a30;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  background-color: #17a8c0;
  border-color: #17a8c0;
  box-shadow: 0 4px 8px rgba(29, 216, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .pageHeader {
    flex-direction: column;
    gap: 15px;
  }

  .pageHeaderTitle {
    font-size: 20px;
    margin-bottom: 15px;
  }

  .text-right {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
  }

  .filterWrapper {
    float: none;
    margin: 0;
    width: 100%;
  }

  .content-section {
    margin: 0 10px;
  }
}

.settings-title {
  color: #484848;
  font-size: 18px;
  font-weight: 600;
}

.settings-options-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.option-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #484848;
  font-size: 14px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #e5f0f2;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.option-toggle:hover {
  border-color: #1dd8f6;
  background: #f4fbfd;
}

.option-toggle:focus-within {
  border-color: #1dd8f6;
  box-shadow: 0 0 0 2px rgba(29, 216, 246, 0.2);
}

.option-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  flex-shrink: 0;
}

.option-label {
  line-height: 1.25;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-error {
  color: #cc0000;
  font-size: 12px;
}

.field-hint {
  color: #6a6a6a;
  font-size: 12px;
  margin: 0;
}

.data-config-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
  margin-top: 8px;
  margin-bottom: 10px;
  overflow: visible;
}

.config-card {
  background: #f8f9fa;
  border: 1px solid #e5f0f2;
  border-radius: 4px;
  padding: 12px;
  overflow: visible;
}

.config-card-title {
  color: #484848;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
}

.ptz-inline-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.ptz-inline-row .config-card-title {
  margin: 0;
}

.ptz-inline-label {
  color: #484848;
  font-size: 13px;
  margin: 0;
}

.ptz-inline-input {
  width: 90px;
}

.ptz-inline-hint {
  margin: 0;
}

.time-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 12px;
}

.duration-input {
  max-width: 140px;
  background: #fff;
}

.duration-input:hover,
.duration-input:focus {
  background: #fff;
}

.button-row {
  margin-top: 20px;
}

.loading-state {
  color: #6a6a6a;
  font-size: 14px;
  padding: 10px 0;
}

/* Tree Select Styling */
.tree-select-days {
  font-family: GTWalsheimPro-Regular, sans-serif;
}

.tree-select-days /deep/ .vue-treeselect__control {
  border: 2px solid #e5f0f2;
  border-radius: 4px;
  background: #fff;
  transition: all 0.2s ease;
  min-height: 40px;
  padding: 8px 10px;
}

.tree-select-days /deep/ .vue-treeselect__control:hover {
  border-color: #1dd8f6;
  background: #f9fcfd;
}

.tree-select-days /deep/ .vue-treeselect__control.vue-treeselect--focused {
  border-color: #1dd8f6;
  box-shadow: 0 0 0 3px rgba(29, 216, 246, 0.15);
}

.tree-select-days /deep/ .vue-treeselect__menu {
  background: #fff;
  border: 1px solid #e5f0f2;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
}

.tree-select-days /deep/ .vue-treeselect__menu-item {
  padding: 10px 15px;
  color: #484848;
  font-size: 13px;
  transition: background-color 0.15s ease;
}

.tree-select-days /deep/ .vue-treeselect__menu-item--hover {
  background: #f4fbfd;
}

.tree-select-days /deep/ .vue-treeselect__menu-item--selected {
  background: #e8f8fb;
  color: #1dd8f6;
  font-weight: 500;
}

/* Parent/Branch styling */
.tree-select-days /deep/ .vue-treeselect__option--branch {
  padding-left: 15px;
  font-weight: 500;
  color: #484848;
}

.tree-select-days /deep/ .vue-treeselect__option--leaf {
  padding-left: 15px;
  color: #484848;
}

/* Option label styling for better visibility */
.tree-select-days /deep/ .vue-treeselect__option-label {
  display: inline;
  flex: 1;
}

.tree-select-days /deep/ .vue-treeselect__option--disabled {
  color: #ccc;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .time-fields-grid {
    grid-template-columns: 1fr;
  }

  .settings-options-list {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- Global styles for vue-treeselect checkboxes - minimal override like Roles.vue -->
<style>
.vue-treeselect__checkbox--indeterminate {
  background: transparent;
  border-color: #e0e0e0;
}

.tree-select-days .vue-treeselect__label-container:hover .vue-treeselect__checkbox--indeterminate {
  background: transparent;
  border-color: #1dd8f6;
}

.tree-select-days .vue-treeselect__minus-mark {
  opacity: 0 !important;
}
</style>
